import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Calculator from "@/components/Calculator";
import RegisterWizard from "@/components/RegisterWizard";
import ContactForm from "@/components/ContactForm";
import Faq from "@/components/Faq";
import { LaunchBenefit } from "@/components/LaunchBenefit";
import { images, isConceptVisual } from "@/content/images";
import { useLanguage } from "@/context/LanguageContext";

export default function Home() {
  const { t } = useLanguage();

  const factTiles = [
    { label: t.hero.factPilot, value: t.hero.factPilotValue, id: "pilot" },
    { label: t.hero.factDevices, value: t.hero.factDevicesValue, id: "devices" },
    { label: t.hero.factPay, value: t.hero.factPayValue, id: "pay" },
  ];

  const imageCaption = isConceptVisual
    ? t.hero.imageCaptionConcept || t.hero.imageCaption
    : t.hero.imageCaption;

  return (
    <div className="bg-[#faf8f5] text-slate-900 antialiased min-h-screen">
      <Seo page="home" />
      <Header />

      {/* Hero */}
      <section id="hero" className="relative hero-grid-pat pt-16 pb-20 border-b spec-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span
                className="text-xs font-mono-spec text-gold-700 font-semibold tracking-[0.18em] uppercase fade-up"
                data-testid="hero-eyebrow"
              >
                {t.hero.eyebrow}
              </span>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-950 leading-[1.08] fade-up fade-up-delay-1"
                data-testid="hero-headline"
              >
                {t.hero.headline}
              </h1>

              <p className="text-lg sm:text-xl text-slate-600 max-w-2xl leading-relaxed fade-up fade-up-delay-2" data-testid="hero-sub">
                {t.hero.sub}
              </p>

              <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-4 fade-up fade-up-delay-3">
                <Link
                  to="/#contact"
                  data-testid="hero-cta-brand"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-slate-950 text-white text-sm font-semibold rounded-full hover:bg-slate-800 transition-all shadow-md active:scale-[0.98]"
                >
                  {t.hero.ctaBrand}
                </Link>
                <Link
                  to="/#register"
                  data-testid="hero-cta-people"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-white border border-slate-300 text-slate-800 text-sm font-semibold rounded-full hover:bg-slate-50 transition-all"
                >
                  {t.hero.ctaPeople}
                </Link>
              </div>

              <div className="pt-1 fade-up fade-up-delay-3">
                <span
                  data-testid="hero-launch-badge"
                  className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-200 rounded-full text-xs font-mono-spec text-blue-800"
                >
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600" />
                  {t.credit.launchBadge}
                </span>
              </div>

              {/* Honest pilot facts (no counters until real data exists) */}
              <div className="pt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 border-t spec-border" data-testid="hero-facts">
                {factTiles.map((f) => (
                  <div key={f.id} className="p-3 bg-white/80 border spec-border rounded-sm">
                    <div className="text-xs text-slate-500 font-mono-spec">{f.label}</div>
                    <div className="text-sm font-semibold text-slate-950 mt-0.5" data-testid={`fact-${f.id}`}>
                      {f.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Product media */}
            <div className="lg:col-span-5 fade-up fade-up-delay-2">
              <div className="relative bg-white border spec-border p-3 shadow-xl rounded-sm">
                <div className="aspect-[4/3] rounded-sm relative overflow-hidden">
                  <img
                    src={images.campus}
                    alt={t.hero.imageAlt}
                    data-testid="hero-image"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="eager"
                  />
                </div>
                <p className="mt-3 text-[11px] text-slate-500 font-mono-spec px-1 pb-1" data-testid="hero-image-caption">
                  {imageCaption}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Targeting one-liner */}
      <div className="bg-white border-b spec-border py-5" data-testid="targetline">
        <p className="max-w-7xl mx-auto px-4 sm:px-6 text-center font-mono-spec text-sm text-slate-700 tracking-wide">
          {t.targetline}
        </p>
      </div>

      {/* How it works */}
      <section id="how-it-works" className="py-20 bg-white border-b spec-border" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-2xl mb-14">
            <span className="text-xs font-mono-spec text-gold-700 font-semibold tracking-wider uppercase">{t.how.eyebrow}</span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 mt-2">{t.how.title}</h2>
            <p className="text-slate-600 mt-3 text-base">{t.how.sub}</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="p-6 sm:p-8 bg-white border spec-border rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-6">
              <h3 className="text-sm font-bold text-slate-950 font-mono-spec tracking-wider border-b spec-border pb-4">
                {t.how.brandsTitle}
              </h3>
              <div className="space-y-4 text-sm">
                {t.how.brandSteps.map((s, i) => (
                  <div key={s.t} className="flex gap-4">
                    <span className="font-mono-spec text-xs text-gold-700 font-bold pt-0.5 shrink-0">0{i + 1}</span>
                    <div>
                      <strong className="text-slate-900 block">{s.t}</strong>
                      <span className="text-slate-600">{s.d}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-6 sm:p-8 bg-white border spec-border rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.03)] space-y-6">
              <h3 className="text-sm font-bold text-slate-950 font-mono-spec tracking-wider border-b spec-border pb-4">
                {t.how.peopleTitle}
              </h3>
              <div className="space-y-4 text-sm">
                {t.how.peopleSteps.map((s, i) => (
                  <div key={s.t} className="flex gap-4">
                    <span className="font-mono-spec text-xs text-gold-700 font-bold pt-0.5 shrink-0">0{i + 1}</span>
                    <div>
                      <strong className="text-slate-900 block">{s.t}</strong>
                      <span className="text-slate-600">{s.d}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs font-mono-spec text-slate-500 max-w-3xl leading-relaxed" data-testid="how-honesty">
            {t.how.honesty}
          </p>
        </div>
      </section>

      {/* Material & specs */}
      <section id="specs" className="py-20 bg-slate-100/70 border-b spec-border" data-testid="specs-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <span className="text-xs font-mono-spec text-slate-500 font-semibold tracking-wider uppercase">{t.specs.eyebrow}</span>
              <h2 className="text-3xl font-bold tracking-tight text-slate-950">{t.specs.title}</h2>
              <p className="text-slate-600 text-base leading-relaxed">{t.specs.sub}</p>

              <div className="grid grid-cols-2 gap-4 pt-2 font-mono-spec text-xs">
                {t.specs.cards.map((c) => (
                  <div key={c.label} className="p-3 bg-white border spec-border">
                    <div className="text-slate-400">{c.label}</div>
                    <div className="text-slate-900 font-bold mt-1">{c.value}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">{c.sub}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-white p-3 border spec-border shadow-md rounded-xl">
                <div className="aspect-[16/10] rounded-lg relative overflow-hidden">
                  <img
                    src={images.desk}
                    alt={t.specs.imageAlt}
                    data-testid="specs-image"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="mt-3 bg-slate-50 border spec-border rounded-lg p-4" data-testid="specs-promise">
                  <div className="text-xs font-mono-spec text-gold-700 uppercase tracking-wider">{t.specs.promiseLabel}</div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 leading-relaxed">{t.specs.promise}</div>
                </div>
                <p className="mt-3 text-[11px] text-slate-500 font-mono-spec px-1 pb-1">
                  {isConceptVisual ? `${t.specs.imageCaption} ${t.hero.imageCaptionConcept}` : t.specs.imageCaption}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Calculator />

      {/* Registration */}
      <section id="register" className="py-20 bg-slate-100/60 border-b spec-border" data-testid="register-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="mb-14">
            <LaunchBenefit />
          </div>
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-mono-spec text-gold-700 font-semibold tracking-wider uppercase">{t.register.eyebrow}</span>
            <h2 className="text-3xl font-bold tracking-tight text-slate-950 mt-2">{t.register.title}</h2>
            <p className="text-slate-600 mt-2 text-sm">{t.register.sub}</p>
          </div>
          <RegisterWizard />
        </div>
      </section>

      {/* Honest proof band */}
      <section className="py-16 bg-white border-b spec-border" data-testid="proof-band">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="p-6 sm:p-8 bg-slate-50 border spec-border rounded-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-1 max-w-2xl">
              <div className="text-xs font-mono-spec text-slate-500">{t.proof.label}</div>
              <div className="text-lg font-bold text-slate-950">{t.proof.title}</div>
              <p className="text-sm text-slate-600">{t.proof.text}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3 shrink-0">
              <span className="px-3 py-1.5 bg-white border spec-border text-xs font-mono-spec text-slate-800">{t.proof.badge1}</span>
              <span className="px-3 py-1.5 bg-gold-50 border border-gold-200 text-xs font-mono-spec text-gold-800">{t.proof.badge2}</span>
            </div>
          </div>
        </div>
      </section>

      <Faq />
      <ContactForm />
      <Footer />
    </div>
  );
}
